import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AuthContext from "../context/AuthContext";
import { Send, Award, Calendar, TrendingUp, Users, CheckCircle, Search, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import nominationsService from "../services/nominationsService";
import usersService from "../services/usersService";
import teamsService from "../services/teamsService";
import { formatError } from "../utils/errorHandler";
import useCustomToast from "../hooks/useCustomToast";
import UserAvatar from "../components/UserAvatar";
import { useTranslation, useFormat } from "../i18n";

const NominationsPage = () => {
  const { t } = useTranslation("nominations");
  const fmt = useFormat();
  const { user: currentUser } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const [activeTab, setActiveTab] = useState("submit"); // submit, sent, received
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    campaign_id: "",
    recipient_id: "",
    recipient_ids: [],
    message: "",
    award_id: "",
    post_to_workvivo: false,
    workvivo_space_id: "",
    workvivo_message_override: "",
  });

  const [searchParams] = useSearchParams();
  const prefillCampaignId = searchParams.get("campaignId");
  const prefillAppliedRef = useRef(false);

  const [selectedCampaignAwards, setSelectedCampaignAwards] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  const [errors, setErrors] = useState({});
  const [recipientSearch, setRecipientSearch] = useState("");
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const recipientDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recipientDropdownRef.current && !recipientDropdownRef.current.contains(event.target)) {
        setShowRecipientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch active campaigns (always refetch on mount so new campaigns appear immediately)
  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ["campaigns", "active"],
    queryFn: () => nominationsService.listActiveCampaigns(),
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!prefillCampaignId || prefillAppliedRef.current || campaigns.length === 0) return;
    if (campaigns.find((c) => c.id === prefillCampaignId)) {
      prefillAppliedRef.current = true;
      setFormData((prev) => ({ ...prev, campaign_id: prefillCampaignId }));
      setActiveTab("submit");
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillCampaignId, campaigns]);

  // Fetch campaign awards and team members when campaign changes
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (formData.campaign_id) {
        // Find the selected campaign
        const selectedCampaign = campaigns.find(c => c.id === formData.campaign_id);

        // Fetch awards
        try {
          const awards = await nominationsService.getActiveCampaignAwards(formData.campaign_id);
          setSelectedCampaignAwards(awards || []);
        } catch (error) {
          console.error("Failed to fetch campaign awards:", error);
          setSelectedCampaignAwards([]);
        }

        // Fetch team members if campaign has team scope
        if (selectedCampaign?.scope === "team" && selectedCampaign?.target_team_id) {
          setLoadingTeamMembers(true);
          try {
            const members = await teamsService.getTeamMembers(selectedCampaign.target_team_id);
            setTeamMembers(members || []);
          } catch (error) {
            console.error("Failed to fetch team members:", error);
            setTeamMembers([]);
          } finally {
            setLoadingTeamMembers(false);
          }
        } else {
          setTeamMembers([]);
        }
      } else {
        setSelectedCampaignAwards([]);
        setTeamMembers([]);
      }
    };
    fetchCampaignData();
  }, [formData.campaign_id, campaigns]);

  // Fetch colleagues for recipient selection (available to all authenticated users)
  const { data: usersData, isLoading: colleaguesLoading } = useQuery({
    queryKey: ["colleagues"],
    queryFn: () => usersService.getColleagues(0, 200),
  });

  // Fetch my nominations (sent)
  const { data: myNominations = [], isLoading: myNominationsLoading } = useQuery({
    queryKey: ["nominations", "mine"],
    queryFn: () => nominationsService.listMine(),
  });

  // Fetch nominations I received
  const { data: receivedNominations = [], isLoading: receivedNominationsLoading } = useQuery({
    queryKey: ["nominations", "received"],
    queryFn: () => nominationsService.listReceived(),
  });

  // Submit nomination mutation
  const submitMutation = useMutation({
    mutationFn: (data) => nominationsService.create(data),
    onSuccess: () => {
      toast.success(t("form.success"));
      setShowModal(false);
      setFormData({
        campaign_id: "",
        recipient_id: "",
        recipient_ids: [],
        message: "",
        award_id: "",
        post_to_workvivo: false,
        workvivo_space_id: "",
        workvivo_message_override: "",
      });
      setSelectedCampaignAwards([]);
      setErrors({});
      queryClient.invalidateQueries({ queryKey: ["nominations"] });
    },
    onError: (error) => {
      toast.error(t("form.submit_failed", { error: formatError(error) }));
    },
  });

  // Handle both array and paginated response formats
  // Debug: console.log("usersData:", usersData);
  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData)) return usersData;
    if (Array.isArray(usersData.data)) return usersData.data;
    // Handle other possible formats
    if (usersData.results && Array.isArray(usersData.results)) return usersData.results;
    if (usersData.items && Array.isArray(usersData.items)) return usersData.items;
    return [];
  }, [usersData]);

  const currentUserId = currentUser?.id?.toString();
  const allColleagues = useMemo(() => {
    return users.filter((user) => user.id?.toString() !== currentUserId);
  }, [users, currentUserId]);

  // Get the selected campaign to determine scope
  const selectedCampaign = formData.campaign_id
    ? campaigns.find(c => c.id === formData.campaign_id)
    : null;

  // Determine recipients based on campaign scope, excluding already-nominated users
  const recipients = useMemo(() => {
    let eligible;
    // If no campaign selected or campaign not found, show all colleagues
    if (!selectedCampaign) {
      eligible = allColleagues;
    } else if (selectedCampaign.scope === "team" && selectedCampaign.target_team_id && teamMembers.length > 0) {
      // For team scope, filter to only team members
      const teamMemberIds = new Set(teamMembers.map(m => (m.user_id || m.id)?.toString()));
      eligible = allColleagues.filter(user => teamMemberIds.has(user.id?.toString()));
    } else {
      // For global scope or when team members haven't loaded yet, show all colleagues
      eligible = allColleagues;
    }

    // Exclude users already nominated for the selected award in this campaign
    if (formData.campaign_id && formData.award_id && myNominations.length > 0) {
      const alreadyNominated = new Set(
        myNominations
          .filter(n => n.campaign_id === formData.campaign_id && n.award_id === formData.award_id)
          .map(n => n.recipient_id)
      );
      eligible = eligible.filter(user => !alreadyNominated.has(user.id));
    }

    return eligible;
  }, [selectedCampaign, allColleagues, teamMembers, formData.campaign_id, formData.award_id, myNominations]);

  // Filter recipients based on search
  const filteredRecipients = recipients.filter((user) => {
    const searchLower = recipientSearch.toLowerCase();
    const fullName = (user.full_name || "").toLowerCase();
    const email = (user.email || "").toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Get selected recipient for display
  const selectedRecipient = recipients.find((u) => u.id === formData.recipient_id);

  const allowMultiple = !!selectedCampaign?.allow_multiple_nominees;
  const selectedRecipients = allColleagues.filter((u) =>
    (formData.recipient_ids || []).includes(u.id)
  );

  const toggleRecipient = (userId) => {
    setFormData((prev) => {
      const current = prev.recipient_ids || [];
      return {
        ...prev,
        recipient_ids: current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      };
    });
  };

  const usersMap = users.reduce((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});

  const campaignMap = campaigns.reduce((acc, campaign) => {
    acc[campaign.id] = campaign;
    return acc;
  }, {});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.campaign_id) {
      newErrors.campaign_id = t("form.errors.campaign_required");
    }

    if (allowMultiple) {
      if ((formData.recipient_ids || []).length === 0) {
        newErrors.recipient_id = t("form.errors.nominee_required");
      }
    } else if (!formData.recipient_id) {
      newErrors.recipient_id = t("form.errors.recipient_required");
    }

    if (!formData.message.trim()) {
      newErrors.message = t("form.errors.message_required");
    } else if (formData.message.length > 2000) {
      newErrors.message = t("form.errors.message_too_long", { max: 2000 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      campaign_id: formData.campaign_id,
      message: formData.message.trim(),
    };
    if (allowMultiple) {
      payload.recipient_ids = formData.recipient_ids;
    } else {
      payload.recipient_id = formData.recipient_id;
    }

    // Include award_id if selected
    if (formData.award_id) {
      payload.award_id = formData.award_id;
    }

    if (formData.post_to_workvivo) {
      payload.post_to_workvivo = true;
      if (formData.workvivo_space_id) {
        payload.workvivo_space_id = formData.workvivo_space_id;
      }
      if (formData.workvivo_message_override?.trim()) {
        payload.workvivo_message_override = formData.workvivo_message_override.trim();
      }
    }

    submitMutation.mutate(payload);
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const renderCampaignCard = (campaign) => {
    const daysRemaining = getDaysRemaining(campaign.end_date);
    const isActive = campaign.status?.toLowerCase() === "active" || campaign.is_active;

    return (
      <motion.div
        key={campaign.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-md p-6 border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{campaign.description}</p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {t(`status.${String(campaign.status || "").toLowerCase()}`, { defaultValue: campaign.status })}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-purple-600" />
            <div>
              <p className="text-xs text-gray-500">{t("campaign_card.days_left")}</p>
              <p className="text-sm font-semibold text-gray-900">{daysRemaining}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-600" />
            <div>
              <p className="text-xs text-gray-500">{t("campaign_card.ranking_pts")}</p>
              <p className="text-sm font-semibold text-gray-900">{t("campaign_card.points_short", { points: fmt.number(campaign.award_points || 0) })}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-600" />
            <div>
              <p className="text-xs text-gray-500">{t("campaign_card.scope")}</p>
              <p className="text-sm font-semibold text-gray-900 capitalize">{t(`scope.${campaign.scope}`, { defaultValue: campaign.scope })}</p>
            </div>
          </div>
        </div>

        {isActive && (
          <button
            onClick={() => {
              setFormData({ ...formData, campaign_id: campaign.id });
              setShowModal(true);
            }}
            className="mt-4 w-full px-4 py-2 nomination-gradient text-white rounded-full transition-all"
          >
            {t("campaign_card.nominate")}
          </button>
        )}
      </motion.div>
    );
  };

  const renderNominationCard = (nomination, isSent = true) => {
    const sender = usersMap[nomination.sender_id];
    const recipient = usersMap[nomination.recipient_id];
    const campaign = campaignMap[nomination.campaign_id];
    const timestamp = nomination.timestamp
      ? fmt.dateTime(nomination.timestamp)
      : null;

    return (
      <div key={nomination.id} className="bg-white rounded-2xl shadow-md p-6 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold text-gray-900">
              {isSent
                ? t("card.you_nominated", { name: recipient?.full_name || recipient?.email || t("card.a_teammate") })
                : t("card.nominated_you", { name: sender?.full_name || sender?.email || t("card.someone") })}
            </p>
            {campaign && (
              <p className="text-sm text-purple-600 mt-1">
                {t("card.campaign", { name: campaign.name })}
              </p>
            )}
          </div>
          {timestamp && <p className="text-xs text-gray-500">{timestamp}</p>}
        </div>

        <div className="border-l-4 border-purple-400 pl-3 py-1 bg-purple-50 rounded">
          <p className="whitespace-pre-line text-gray-700">{nomination.message}</p>
        </div>

        {nomination.award && (
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-purple-500" />
            <p className="text-sm text-gray-600">
              {t("card.award_points", { name: nomination.award.name, points: fmt.number(nomination.award.points_value || 0) })}
            </p>
          </div>
        )}

        {nomination.workvivo_post_status === "posted" && (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            {t("card.posted_to_workvivo")}
          </span>
        )}
      </div>
    );
  };

  if (campaignsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Active Campaigns Section */}
        {campaigns.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">{t("list.active_campaigns")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map(renderCampaignCard)}
            </div>
          </div>
        )}

        {campaigns.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 sm:p-6">
            <p className="text-amber-800 text-sm sm:text-base">
              {t("list.no_active_campaigns")}
            </p>
          </div>
        )}

        {(() => {
          const sentList = (
            <div className="space-y-4">
              {myNominationsLoading ? (
                <div className="flex justify-center py-8 sm:py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
                </div>
              ) : myNominations.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-md p-6 sm:p-12 text-center">
                  <Send className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm sm:text-base">{t("list.no_sent")}</p>
                </div>
              ) : (
                myNominations.map((nom) => renderNominationCard(nom, true))
              )}
            </div>
          );

          const receivedList = (
            <div className="space-y-4">
              {receivedNominationsLoading ? (
                <div className="flex justify-center py-8 sm:py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
                </div>
              ) : receivedNominations.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-md p-6 sm:p-12 text-center">
                  <Award className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm sm:text-base">{t("list.no_received")}</p>
                </div>
              ) : (
                receivedNominations.map((nom) => renderNominationCard(nom, false))
              )}
            </div>
          );

          return (
            <>
              {/* Desktop: tabbed view */}
              <div className="hidden sm:block space-y-8">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab("sent")}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === "sent"
                          ? "border-purple-600 text-purple-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {t("list.my_nominations", { count: myNominations.length })}
                    </button>
                    <button
                      onClick={() => setActiveTab("received")}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === "received"
                          ? "border-purple-600 text-purple-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {t("list.received_nominations", { count: receivedNominations.length })}
                    </button>
                  </nav>
                </div>
                <div>{activeTab === "sent" ? sentList : receivedList}</div>
              </div>

              {/* Mobile: both sections stacked */}
              <div className="sm:hidden space-y-6">
                <section>
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4 text-purple-600" />
                    {t("list.my_nominations", { count: myNominations.length })}
                  </h3>
                  {sentList}
                </section>
                <section>
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-purple-600" />
                    {t("list.received_nominations", { count: receivedNominations.length })}
                  </h3>
                  {receivedList}
                </section>
              </div>
            </>
          );
        })()}

        {/* Nomination Modal */}
        <AnimatePresence>
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={() => !submitMutation.isLoading && setShowModal(false)}
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t("form.title")}</h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Campaign Selection */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("form.campaign_label")} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="campaign_id"
                        value={formData.campaign_id}
                        onChange={(e) => {
                          handleInputChange(e);
                          setFormData(prev => ({ ...prev, campaign_id: e.target.value, award_id: "", recipient_id: "", recipient_ids: [] }));
                          setRecipientSearch("");
                        }}
                        disabled={submitMutation.isLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t("form.select_campaign")}</option>
                        {campaigns.map((campaign) => (
                          <option key={campaign.id} value={campaign.id}>
                            {campaign.name}
                          </option>
                        ))}
                      </select>
                      {errors.campaign_id && (
                        <p className="text-sm text-red-600">{errors.campaign_id}</p>
                      )}
                    </div>

                    {/* Campaign Awards Selection */}
                    {selectedCampaignAwards.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("form.award_category")}
                        </label>
                        <div className="grid grid-cols-2 gap-2 rounded-2xl">
                          {selectedCampaignAwards.map((award) => {
                            const colorFrom = award.color_from?.startsWith("#") ? award.color_from : (award.color_from?.match(/\[(.*?)\]/)?.[1] || "#a855f7");
                            const colorTo = award.color_to?.startsWith("#") ? award.color_to : (award.color_to?.match(/\[(.*?)\]/)?.[1] || "#3b82f6");
                            const isSelected = formData.award_id === award.id;

                            return (
                              <button
                                key={award.id}
                                type="button"
                                onClick={() => setFormData({ ...formData, award_id: award.id })}
                                disabled={submitMutation.isLoading}
                                className={`flex items-center gap-3 p-3 rounded-full border-2 transition-all ${
                                  isSelected
                                    ? "border-purple-500 bg-purple-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <div
                                  className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-sm flex-shrink-0"
                                  style={{ background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}
                                >
                                  {award.emoji && award.emoji.startsWith('/api/v1/recognition-type-icons/') ? <img src={award.emoji} alt="" className="w-6 h-6 object-contain" /> : (award.emoji || "⭐")}
                                </div>
                                <div className="text-left min-w-0">
                                  <div className="font-medium text-gray-900 text-sm truncate">{award.name}</div>
                                  {award.points_value > 0 && (
                                    <div className="text-xs text-gray-500">{t("ranking_pts", { count: award.points_value })}</div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Recipient Selection - Searchable Dropdown */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("form.nominate_label")} <span className="text-red-500">*</span>
                        {allowMultiple && (
                          <span className="ml-2 text-xs font-normal text-purple-600">
                            {t("form.multiple_hint")}
                          </span>
                        )}
                      </label>
                      {allowMultiple && selectedRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-1">
                          {selectedRecipients.map((u) => (
                            <span
                              key={u.id}
                              className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 rounded-full pl-2 pr-1 py-1 text-xs font-medium text-purple-800"
                            >
                              {u.full_name || u.email}
                              <button
                                type="button"
                                onClick={() => toggleRecipient(u.id)}
                                className="p-0.5 rounded-full hover:bg-purple-100"
                                aria-label={t("form.remove_recipient", { name: u.full_name || u.email })}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="relative" ref={recipientDropdownRef}>
                        {/* Selected value or search input */}
                        <div
                          className={`w-full px-3 py-2 border rounded-2xl flex items-center justify-between cursor-pointer ${
                            showRecipientDropdown ? "border-purple-500 ring-2 ring-purple-500" : "border-gray-300"
                          }`}
                          onClick={() => !submitMutation.isLoading && setShowRecipientDropdown(true)}
                        >
                          {showRecipientDropdown ? (
                            <div className="flex items-center gap-2 w-full">
                              <Search className="w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                value={recipientSearch}
                                onChange={(e) => setRecipientSearch(e.target.value)}
                                placeholder={t("form.search_placeholder")}
                                className="flex-1 outline-none bg-transparent"
                                autoFocus
                                disabled={submitMutation.isLoading}
                              />
                            </div>
                          ) : (
                            <>
                              <span className={(allowMultiple ? selectedRecipients.length > 0 : selectedRecipient) ? "text-gray-900" : "text-gray-500"}>
                                {allowMultiple
                                  ? selectedRecipients.length > 0
                                    ? t("form.teammates_selected", { count: selectedRecipients.length })
                                    : t("form.select_teammates")
                                  : selectedRecipient
                                    ? (selectedRecipient.full_name || selectedRecipient.email)
                                    : t("form.select_teammate")}
                              </span>
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </>
                          )}
                          {selectedRecipient && !showRecipientDropdown && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormData({ ...formData, recipient_id: "" });
                              }}
                              className="ml-2 p-1 hover:bg-gray-100 rounded"
                            >
                              <X className="w-4 h-4 text-gray-400" />
                            </button>
                          )}
                        </div>

                        {/* Dropdown list */}
                        <AnimatePresence>
                          {showRecipientDropdown && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg max-h-60 overflow-y-auto"
                            >
                              {colleaguesLoading || loadingTeamMembers ? (
                                <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                                  <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                                  {colleaguesLoading ? t("form.loading_colleagues") : t("form.loading_team_members")}
                                </div>
                              ) : filteredRecipients.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  {recipientSearch
                                    ? t("form.no_match")
                                    : selectedCampaign?.scope === "team"
                                      ? t("form.no_team_members")
                                      : t("form.no_teammates")}
                                </div>
                              ) : (
                                filteredRecipients.map((user) => (
                                  <div
                                    key={user.id}
                                    className={`px-4 py-3 cursor-pointer hover:bg-purple-50 transition-colors flex items-center gap-3 ${
                                      (allowMultiple ? (formData.recipient_ids || []).includes(user.id) : formData.recipient_id === user.id) ? "bg-purple-100" : ""
                                    }`}
                                    onClick={() => {
                                      if (allowMultiple) {
                                        toggleRecipient(user.id);
                                      } else {
                                        setFormData({ ...formData, recipient_id: user.id });
                                        setRecipientSearch("");
                                        setShowRecipientDropdown(false);
                                      }
                                    }}
                                  >
                                    <UserAvatar
                                      src={user.avatar_url}
                                      name={user.full_name || user.email}
                                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                      textClassName="text-xs"
                                    />
                                    <div>
                                      <div className="font-medium text-gray-900">{user.full_name || t("form.no_name")}</div>
                                      <div className="text-sm text-gray-500">{user.email}</div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      {errors.recipient_id && (
                        <p className="text-sm text-red-600">{errors.recipient_id}</p>
                      )}
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("form.message_label")} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder={t("form.message_placeholder")}
                        disabled={submitMutation.isLoading}
                        rows={5}
                        maxLength={2000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                          {t("form.char_count", { used: fmt.number(formData.message.length), max: fmt.number(2000) })}
                        </p>
                      </div>
                      {errors.message && <p className="text-sm text-red-600">{errors.message}</p>}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        disabled={submitMutation.isLoading}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 disabled:opacity-50"
                      >
                        {t("common:actions.cancel")}
                      </button>
                      <button
                        type="submit"
                        disabled={submitMutation.isLoading}
                        className="flex-1 px-4 py-2 nomination-gradient text-white rounded-full transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submitMutation.isLoading ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            {t("form.submitting")}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            {t("common:actions.submit")}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NominationsPage;
